/**
 * GLAMRDiP Image Lightbox — fullscreen PDP image viewer (vanilla JS).
 *
 * STRUCTURAL CONTRACT (Joel 2026-04-30 refactor):
 *
 *   The lightbox renders an EXPLICIT array of images passed at open() time.
 *   It does NOT auto-discover images via DOM selectors. This is enforced by
 *   the API: open() takes `{ images, startIndex, returnFocus }` and refuses
 *   to mount without an `images` array.
 *
 *   Why: the previous "find every [data-lightbox-trigger] on the page and
 *   open one of them" pattern leaked images across surfaces (community
 *   gallery into PDP, upsell slides into PDP, etc.) every time a new
 *   surface added the trigger attribute. The fix isn't another scope
 *   patch — it's removing the discovery step entirely. Callers tell the
 *   lightbox what to show. There is no auto-discovery.
 *
 *   Public API:
 *     window.__gdLightbox.open({
 *       images:    [{ full: 'url', alt: 'string' }, ...],   // REQUIRED
 *       startIndex: 0,                                       // optional, default 0
 *       returnFocus: HTMLElement                              // optional
 *     });
 *
 *   Section-bound triggers (data-lightbox-trigger + data-image-full +
 *   data-lightbox-scope on a wrapping ancestor) still work via a shim
 *   click handler that COLLECTS the scoped siblings into an explicit
 *   images array and forwards to open(). The shim is the only place
 *   "scoped sibling discovery" happens; everything else uses the array.
 *
 * Page-speed contract:
 *   - Modal <img> starts with empty src; src set only on user click.
 *   - Adjacent images preloaded after open (idx-1 + idx+1) so prev/next
 *     nav is instant after the first interaction.
 *   - All images decoding="async"; no full-res bytes on initial page load.
 *
 * UX:
 *   - Click image → 2x zoom toggle (origin = click point).
 *   - Mouse wheel → zoom in/out (1×–4×).
 *   - Drag while zoomed → pan.
 *   - Touch swipe horizontal → prev/next; swipe down → close.
 *   - Keyboard ←/→/Esc.
 *   - Backdrop click → close (native <dialog> behaviour delegated).
 *
 * Accessibility:
 *   - role="dialog" + aria-modal supplied by native <dialog>.
 *   - Focus returned to triggering element on close.
 *   - All controls aria-labelled.
 */
(() => {
  'use strict';

  class GDLightbox {
    constructor(modal) {
      this.modal      = modal;
      this.image      = modal.querySelector('[data-lightbox-image]');
      this.counter    = modal.querySelector('[data-lightbox-counter]');
      this.stage      = modal.querySelector('[data-lightbox-stage]');
      this.btnClose   = modal.querySelector('[data-lightbox-close]');
      this.btnPrev    = modal.querySelector('[data-lightbox-prev]');
      this.btnNext    = modal.querySelector('[data-lightbox-next]');
      // STATE — single source of truth.
      // images[]: array of { full, alt }. Set ONLY by open(); next/prev
      // mutate idx, never images. _render reads images[idx] for src/alt
      // and images.length for the counter.
      this.images       = [];
      this.idx          = 0;
      this._returnFocus = null;
      this.zoom       = 1;
      this.tx         = 0;
      this.ty         = 0;
      this._preloadCache = new Set();
      this._bindModal();
      this._bindShimTriggers();
    }

    /**
     * Discover any [data-lightbox-trigger] elements on the page and bind
     * a SHIM click handler that builds an explicit images array from
     * scoped siblings, then forwards to open(). This keeps the
     * declarative section markup pattern working while the API itself
     * never auto-discovers — open() always receives an explicit array.
     */
    _bindShimTriggers() {
      const triggers = document.querySelectorAll('[data-lightbox-trigger]');
      triggers.forEach((t) => {
        if (t.dataset.gdLightboxBound === '1') return;
        t.dataset.gdLightboxBound = '1';
        t.addEventListener('click', (e) => {
          e.preventDefault();
          // Find the scope ancestor; siblings inside that scope = the
          // gallery the user is viewing. Triggers without a scope are
          // grouped with other un-scoped triggers (legacy fallback).
          const scope = t.closest('[data-lightbox-scope]');
          const siblings = scope
            ? [...scope.querySelectorAll('[data-lightbox-trigger]')]
            : [...document.querySelectorAll('[data-lightbox-trigger]')]
                .filter(x => !x.closest('[data-lightbox-scope]'));
          // Sort by data-lightbox-index so explicit numeric ordering
          // (set by the section template) wins over DOM order.
          siblings.sort((a, b) => {
            const ai = parseInt(a.dataset.lightboxIndex, 10) || 0;
            const bi = parseInt(b.dataset.lightboxIndex, 10) || 0;
            return ai - bi;
          });
          const images = siblings.map((el) => ({
            full: el.dataset.imageFull || el.querySelector('img')?.src || '',
            alt:  el.dataset.imageAlt || '',
          }));
          const startIndex = Math.max(0, siblings.indexOf(t));
          this.open({ images, startIndex, returnFocus: t });
        });
      });
    }

    _bindModal() {
      // Modal-level handlers bind ONCE per modal — guard via dataset so
      // re-init on shopify:section:load doesn't add duplicate listeners.
      // Without this guard, each section reload appended another `next`
      // / `prev` handler, so a single chevron click fired the handler
      // N times → idx jumped by N → counter showed "1 / 12" → "3 / 12"
      // → "5 / 12" instead of stepping sequentially.
      if (this.modal.dataset.gdLightboxBound === '1') return;
      this.modal.dataset.gdLightboxBound = '1';

      this.btnClose?.addEventListener('click', () => this.close());
      this.btnPrev?.addEventListener('click', () => this.prev());
      this.btnNext?.addEventListener('click', () => this.next());

      // Backdrop click — when user clicks on the dialog itself (not its
      // children), close. Native <dialog> ::backdrop catches outside-clicks
      // via the dialog element being the event target.
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) this.close();
      });

      // Keyboard
      this.modal.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.next(); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); this.prev(); }
        // Esc handled natively by <dialog>; we hook close() via the
        // dialog 'close' event below for cleanup.
      });

      // Native dialog 'close' fires on Esc + close() call. Use it for
      // shared cleanup (focus return, body scroll restore, zoom reset).
      this.modal.addEventListener('close', () => this._afterClose());

      // Click image → zoom toggle.
      this.image?.addEventListener('click', (e) => {
        e.stopPropagation();
        this._toggleZoom(e);
      });

      // Wheel zoom (desktop) — zoom in/out 1× to 4×.
      this.stage?.addEventListener('wheel', (e) => {
        if (e.deltaY === 0) return;
        e.preventDefault();
        const dir = e.deltaY < 0 ? 1 : -1;
        this._setZoom(this.zoom + dir * 0.25, e);
      }, { passive: false });

      // Pan when zoomed (drag).
      let panStartX = null, panStartY = null, panStartTx = 0, panStartTy = 0;
      this.image?.addEventListener('mousedown', (e) => {
        if (this.zoom <= 1) return;
        e.preventDefault();
        panStartX = e.clientX; panStartY = e.clientY;
        panStartTx = this.tx; panStartTy = this.ty;
      });
      window.addEventListener('mousemove', (e) => {
        if (panStartX === null) return;
        this.tx = panStartTx + (e.clientX - panStartX);
        this.ty = panStartTy + (e.clientY - panStartY);
        this._applyTransform();
      });
      window.addEventListener('mouseup', () => { panStartX = panStartY = null; });

      // Touch swipe — horizontal nav, vertical-down close.
      let tStartX = null, tStartY = null, tStartedAt = 0;
      this.stage?.addEventListener('touchstart', (e) => {
        if (e.touches.length !== 1) return;
        tStartX = e.touches[0].clientX;
        tStartY = e.touches[0].clientY;
        tStartedAt = performance.now();
      }, { passive: true });
      this.stage?.addEventListener('touchend', (e) => {
        if (tStartX === null) return;
        const dx = (e.changedTouches[0]?.clientX ?? tStartX) - tStartX;
        const dy = (e.changedTouches[0]?.clientY ?? tStartY) - tStartY;
        const dt = performance.now() - tStartedAt;
        tStartX = tStartY = null;
        if (dt > 700) return;
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) && this.zoom <= 1) {
          if (dx < 0) this.next(); else this.prev();
        } else if (dy > 80 && Math.abs(dy) > Math.abs(dx)) {
          this.close();
        }
      }, { passive: true });
    }

    /**
     * Public entry point. REQUIRES an explicit images array.
     * Throws (in dev) / no-ops (in prod) if `images` is missing —
     * intentionally loud to make the contract impossible to miss.
     *
     * @param {{images: Array<{full:string,alt?:string}>, startIndex?: number, returnFocus?: HTMLElement}} opts
     */
    open(opts) {
      if (!opts || !Array.isArray(opts.images)) {
        console.error('[gd-lightbox] open() requires { images: [...] }. Refusing to mount.', opts);
        return;
      }
      this.images = opts.images.filter(Boolean);
      if (this.images.length === 0) {
        console.error('[gd-lightbox] open() called with empty images array. Refusing to mount.');
        return;
      }
      this.idx = clamp(opts.startIndex || 0, 0, this.images.length - 1);
      this._returnFocus = opts.returnFocus || null;
      this._render();
      this._preloadAdjacent();
      // Native dialog API — gives focus trap + Esc for free.
      const wasOpen = this.modal.open;
      if (typeof this.modal.showModal === 'function') {
        if (!wasOpen) this.modal.showModal();
      } else {
        this.modal.setAttribute('open', '');
      }
      // Joel 2026-04-30: migrate to shared scroll-lock manager so lightbox
      // open inside a chained modal flow (bundle builder → upsell →
      // tap image to expand) doesn't double-release the body lock when
      // the lightbox closes. Reference-counted manager keeps body
      // locked while ANY overlay is open.
      if (!wasOpen) {
        if (window.gdScrollLock) window.gdScrollLock.lock(this.stage || this.modal);
        else document.body.style.overflow = 'hidden';
      }
      this.btnClose?.focus?.();
    }

    close() {
      if (typeof this.modal.close === 'function' && this.modal.open) {
        this.modal.close();   // triggers 'close' event → _afterClose
      } else {
        this.modal.removeAttribute('open');
        this._afterClose();
      }
    }

    _afterClose() {
      // Match the shared-manager unlock if open() used it; fall back to
      // raw overflow reset for environments where gdScrollLock isn't
      // available. The lock guard above pairs with this unlock so the
      // ref counter increments + decrements exactly once per open/close.
      if (window.gdScrollLock) window.gdScrollLock.unlock(this.stage || this.modal);
      else document.body.style.overflow = '';
      this.zoom = 1; this.tx = 0; this.ty = 0;
      this._applyTransform();
      // Clear src on close to free memory (high-res images can be ~500KB+).
      if (this.image) this.image.src = '';
      // preventScroll so the browser doesn't auto-scroll to bring the
      // returned-focus element into view — body scroll-lock has already
      // restored the user to their pre-modal position (Joel 2026-05-01).
      try { this._returnFocus?.focus?.({ preventScroll: true }); } catch { this._returnFocus?.focus?.(); }
      this._returnFocus = null;
      // Reset state on close so the next open() initializes from its
      // own startIndex — no stale leak between unrelated open() calls.
      this.images = [];
      this.idx = 0;
    }

    next() {
      if (this.images.length <= 1) return;
      this.idx = (this.idx + 1) % this.images.length;
      this._render();
      this._preloadAdjacent();
    }
    prev() {
      if (this.images.length <= 1) return;
      this.idx = (this.idx - 1 + this.images.length) % this.images.length;
      this._render();
      this._preloadAdjacent();
    }

    _render() {
      const it = this.images[this.idx];
      if (!it || !this.image) return;
      this.image.src = it.full || '';
      this.image.alt = it.alt || '';
      if (this.counter) this.counter.textContent = `${this.idx + 1} / ${this.images.length}`;
      this.zoom = 1; this.tx = 0; this.ty = 0;
      this._applyTransform();
      // Hide nav arrows when only 1 image.
      const single = this.images.length <= 1;
      if (this.btnPrev) this.btnPrev.hidden = single;
      if (this.btnNext) this.btnNext.hidden = single;
    }

    _preloadAdjacent() {
      const len = this.images.length;
      if (len <= 1) return;
      const next = (this.idx + 1) % len;
      const prev = (this.idx - 1 + len) % len;
      [next, prev].forEach((i) => {
        const url = this.images[i]?.full;
        if (!url || this._preloadCache.has(url)) return;
        this._preloadCache.add(url);
        const im = new Image();
        im.decoding = 'async';
        im.src = url;
      });
    }

    _toggleZoom(e) {
      if (this.zoom > 1) {
        this._setZoom(1, e);
      } else {
        this._setZoom(2, e);
      }
    }

    _setZoom(level, e) {
      const next = clamp(level, 1, 4);
      if (next === this.zoom) return;
      // When zooming, set transform-origin to the click/wheel point so the
      // zoom happens around the cursor rather than the image center.
      if (e && this.image) {
        const rect = this.image.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        this.image.style.transformOrigin = `${clamp(x, 0, 100)}% ${clamp(y, 0, 100)}%`;
      }
      this.zoom = next;
      // Reset pan when fully zoomed out.
      if (next <= 1) { this.tx = 0; this.ty = 0; }
      this._applyTransform();
      // Cursor reflects state.
      if (this.image) this.image.style.cursor = next > 1 ? 'grab' : 'zoom-in';
    }

    _applyTransform() {
      if (!this.image) return;
      this.image.style.transform = `translate(${this.tx}px, ${this.ty}px) scale(${this.zoom})`;
    }
  }

  function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

  function init() {
    // Guard so missing markup / unexpected throw doesn't break the page
    // (especially Shopify theme editor — unhandled exceptions surface as
    // "Page failed to load due to an error").
    try {
      const modal = document.querySelector('[data-lightbox]');
      if (!modal) return;
      if (window.__gdLightbox) {
        // Re-bind any newly-mounted shim triggers (dynamic sections,
        // upsell modal, etc.). Modal handlers stay bound exactly once
        // via the dataset guard inside _bindModal.
        window.__gdLightbox._bindShimTriggers();
        return;
      }
      window.__gdLightbox = new GDLightbox(modal);
    } catch (e) {
      console.error('[gd-image-lightbox] init failed:', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', () => init());
})();
